/**
 * Brings the game socket back after the server closes it on purpose.
 *
 * socket.io-client retries on its own after a network drop, but never after
 * the server disconnects the socket ('io server disconnect'), which is exactly
 * what a deploy does (server.js drains the round, then calls
 * io.disconnectSockets(true)). Without this the app sat on "Reconnecting…"
 * until it was killed and relaunched.
 *
 * The first attempt waits for the restart window the server announced in
 * 'server:restarting' (20 s by default), later attempts double up to 60 s,
 * and returning to the app (tab visible again, or the native 'resume' event)
 * retries straight away. A session the server ended is never reopened.
 *
 * Every client is disconnected in the same instant, so each delay is stretched
 * by a random 0–50 %: the reconnections spread over the following seconds
 * instead of all landing on the new server (and on the per-address handshake
 * limit) at once. Never earlier than the announced window.
 */

/** The parts of a socket.io client socket this drives. */
export interface ReconnectableSocket {
  readonly connected: boolean;
  /** True while the socket (or its manager) is connected or still retrying. */
  readonly active: boolean;
  connect(): unknown;
  disconnect(): unknown;
}

export const DEFAULT_RESTART_RETRY_MS = 20_000;
export const MAX_RESTART_RETRY_MS = 60_000;
/** visibilitychange and the native 'resume' event usually arrive together. */
export const RESUME_DEBOUNCE_MS = 2_000;
/** Each delay is lengthened by up to this fraction, at random. */
export const RESTART_JITTER = 0.5;

export const SERVER_DISCONNECT_REASON = 'io server disconnect';

export interface RestartReconnectorOptions {
  socket: ReconnectableSocket;
  hasSessionEnded: () => boolean;
  now?: () => number;
  setTimer?: (callback: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** A number in [0, 1), as Math.random returns. */
  random?: () => number;
}

export interface RestartReconnector {
  /** The server announced a restart; use its retry window for the next drop. */
  noteServerRestarting: (retryAfterMs?: unknown) => void;
  onDisconnect: (reason: string) => void;
  /** The transport is back; stop the pending attempt. */
  onConnect: () => void;
  /** The server accepted the session (game:init); later drops start from scratch. */
  onReady: () => void;
  /** The player came back to the app: retry now if we are not connected. */
  resume: () => void;
  dispose: () => void;
  readonly pending: boolean;
}

export function restartRetryMs(value: unknown): number {
  const ms = typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : DEFAULT_RESTART_RETRY_MS;
  return Math.min(ms, MAX_RESTART_RETRY_MS);
}

export function createRestartReconnector({
  socket,
  hasSessionEnded,
  now = () => Date.now(),
  setTimer = (callback, ms) => setTimeout(callback, ms),
  clearTimer = handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
  random = Math.random,
}: RestartReconnectorOptions): RestartReconnector {
  let retryAfterMs = DEFAULT_RESTART_RETRY_MS;
  let attempts = 0;
  let timer: unknown = null;
  let lastResume = -Infinity;
  let disposed = false;

  const stopped = () => disposed || socket.connected || hasSessionEnded();
  const cancel = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const nextDelay = () => {
    const base = Math.min(MAX_RESTART_RETRY_MS, retryAfterMs * 2 ** attempts++);
    return Math.round(base * (1 + RESTART_JITTER * Math.min(Math.max(random(), 0), 1)));
  };
  const schedule = () => {
    cancel();
    timer = setTimer(attempt, nextDelay());
  };
  function attempt() {
    timer = null;
    if (stopped()) return;
    // An active socket is already retrying through its manager; leave it be
    // and only check again later.
    if (!socket.active) socket.connect();
    schedule();
  }

  return {
    noteServerRestarting(value) {
      retryAfterMs = restartRetryMs(value);
    },
    onDisconnect(reason) {
      if (reason !== SERVER_DISCONNECT_REASON || stopped()) return;
      schedule();
    },
    onConnect() {
      cancel();
    },
    onReady() {
      attempts = 0;
      retryAfterMs = DEFAULT_RESTART_RETRY_MS;
    },
    resume() {
      if (stopped()) return;
      const at = now();
      if (at - lastResume < RESUME_DEBOUNCE_MS) return;
      lastResume = at;
      // A manager that is waiting out its own backoff ignores connect(), so
      // reset it first; the attempt then starts immediately.
      if (socket.active) socket.disconnect();
      socket.connect();
    },
    dispose() {
      disposed = true;
      cancel();
    },
    get pending() {
      return timer !== null;
    },
  };
}
